import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import { z } from 'zod';
import { first } from 'lodash';

import { TextField, TextFieldProps } from '@mui/material';

import { useSelector, useDispatch } from '../store/utils';
import { variablesChange } from '../store/actions';
import { VariableId } from '../store/types';

import { ControlBox, ControlBoxProps } from '../atoms/control-box';

type Props = {
  inputVariableId: VariableId;
  size?: 'normal' | 'tall';
  schema?: z.Schema;
  inputWidth?: number;
} & Pick<TextFieldProps, 'placeholder' | 'label' | 'type'> &
  ControlBoxProps;

export const InputBox = ({
  inputVariableId,
  placeholder,
  label,
  schema,
  inputWidth = 170,
  type,
  ...props
}: Props) => {
  const storeValue = useSelector((s) =>
    inputVariableId ? get(s.variables, inputVariableId) : undefined
  );
  const dispatch = useDispatch();

  const [value, setValue] = useState(storeValue || '');
  const [error, setError] = useState<z.ZodIssue | undefined>();

  useEffect(() => {
    setValue(storeValue);
    setError(undefined);
  }, [storeValue]);

  useEffect(() => {
    if (!schema) {
      setError(undefined);
      return;
    }

    const parsed = schema.safeParse(value);
    if (!parsed.success) {
      setError(first(parsed.error.issues));
      return;
    }

    setValue(parsed.data);
    setError(undefined);
  }, [value, schema]);

  const submit = () => {
    if (schema && error) return;

    dispatch(variablesChange({ [inputVariableId]: value }));
  };

  return (
    <ControlBox
      control={
        <TextField
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') submit();
          }}
          onBlur={submit}
          sx={{ ml: 'auto', width: inputWidth }}
          placeholder={placeholder}
          label={label}
          type={type}
          size="small"
          error={Boolean(error)}
          helperText={error?.message}
        />
      }
      {...props}
    />
  );
};
